import React, { useState, useEffect } from "react";

const Payroll = () => {
  // Example state for employees and payroll entries
  const [employees, setEmployees] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [newEmployee, setNewEmployee] = useState({ name: "", rate: "" });
  // payPeriodStart and payPeriodEnd for calendar selection
  const [newPayroll, setNewPayroll] = useState({ employeeId: "", hours: "", payPeriodStart: "", payPeriodEnd: "", localTax: "" });
  // Deduction rate (percent, e.g. 5 for 5%) - legacy, not used for auto now
  // eslint-disable-next-line no-unused-vars
  const [deductionRate, setDeductionRate] = useState(5);

  // Default local tax rate (for St. Louis/KC, e.g. 1%)
  const [defaultLocalTaxRate, setDefaultLocalTaxRate] = useState(1);

  // Placeholder: Load employees/payroll from Firebase here
  useEffect(() => {
    // TODO: Fetch from Firebase
  }, []);

  const addEmployee = (e) => {
    e.preventDefault();
    if (!newEmployee.name || !newEmployee.rate) return;
    setEmployees([...employees, { id: Date.now(), ...newEmployee, rate: parseFloat(newEmployee.rate) }]);
    setNewEmployee({ name: "", rate: "" });
  };

  // Helper functions for tax calculations
  // 2025 FICA rates
  const calcFICA = (gross) => ({
    social: gross * 0.062,
    medicare: gross * 0.0145,
  });
  // Simple federal tax estimator (single, 2025, no dependents, annualized, 12% bracket for demo)
  const calcFederal = (gross) => {
    // Annualize gross for bracket, then de-annualize
    const annual = gross * 26; // biweekly
    let fed = 0;
    if (annual <= 11000) fed = 0;
    else if (annual <= 44725) fed = (annual - 11000) * 0.12;
    else fed = (44725 - 11000) * 0.12 + (annual - 44725) * 0.22;
    return fed / 26;
  };
  // Missouri state tax estimator (2025, single, no dependents, annualized, top rate 4.8%)
  const calcMOState = (gross) => {
    const annual = gross * 26;
    let state = 0;
    if (annual <= 1121) state = 0;
    else if (annual <= 9000) state = (annual - 1121) * 0.015;
    else state = (9000 - 1121) * 0.015 + (annual - 9000) * 0.048;
    return state / 26;
  };
  // Local tax (user can override per entry)
  const calcLocal = (gross, localRate) => gross * (localRate / 100);

  const addPayroll = (e) => {
    e.preventDefault();
    if (!newPayroll.employeeId || !newPayroll.hours || !newPayroll.payPeriodStart || !newPayroll.payPeriodEnd) return;
    const emp = employees.find((e) => e.id === Number(newPayroll.employeeId));
    const rate = emp ? emp.rate : 0;
    const hours = parseFloat(newPayroll.hours);
    const gross = rate * hours;
    // Calculate deductions
    const fica = calcFICA(gross);
    const federal = calcFederal(gross);
    const state = calcMOState(gross);
    const localRate = newPayroll.localTax === '' || newPayroll.localTax == null ? defaultLocalTaxRate : parseFloat(newPayroll.localTax);
    const local = calcLocal(gross, localRate);
    const totalDeduction = fica.social + fica.medicare + federal + state + local;
    setPayroll([
      ...payroll,
      {
        ...newPayroll,
        hours,
        gross,
        ficaSocial: fica.social,
        ficaMedicare: fica.medicare,
        federal,
        state,
        local,
        localRate,
        totalDeduction,
        net: gross - totalDeduction,
        id: Date.now(),
      },
    ]);
    setNewPayroll({ employeeId: "", hours: "", payPeriodStart: "", payPeriodEnd: "", localTax: "" });
  };

  // Add mock data for demo/testing
  const addMockData = () => {
    const mockEmployees = [
      { id: 1, name: "John Doe", rate: 20 },
      { id: 2, name: "Jane Smith", rate: 22 },
      { id: 3, name: "Alex Green", rate: 18 },
    ];
    // Use the same logic as addPayroll for mock payroll
    const makePayroll = (id, employeeId, hours, payPeriodStart, payPeriodEnd, localTax) => {
      const emp = mockEmployees.find((e) => e.id === employeeId);
      const rate = emp ? emp.rate : 0;
      const gross = rate * hours;
      const fica = calcFICA(gross);
      const federal = calcFederal(gross);
      const state = calcMOState(gross);
      const localRate = localTax == null ? defaultLocalTaxRate : localTax;
      const local = calcLocal(gross, localRate);
      const totalDeduction = fica.social + fica.medicare + federal + state + local;
      return {
        id,
        employeeId,
        hours,
        payPeriodStart,
        payPeriodEnd,
        gross,
        ficaSocial: fica.social,
        ficaMedicare: fica.medicare,
        federal,
        state,
        local,
        localRate,
        totalDeduction,
        net: gross - totalDeduction,
      };
    };
    const mockPayroll = [
      makePayroll(101, 1, 40, "2025-11-01", "2025-11-15", 1),
      makePayroll(102, 2, 38, "2025-11-01", "2025-11-15", 1),
      makePayroll(103, 3, 42, "2025-11-01", "2025-11-15", 1),
    ];
    setEmployees(mockEmployees);
    setPayroll(mockPayroll);
  };

  const getEmployeeName = (id) => {
    const emp = employees.find((e) => e.id === Number(id));
    return emp ? emp.name : "";
  };

  return (
    <div className="p-2 sm:p-4 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Payroll Management</h2>

      {/* Mock Data Button */}
      <button
        className="mb-4 bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded text-sm"
        onClick={addMockData}
      >
        Add Mock Data
      </button>

      {/* Add Employee */}
      <form onSubmit={addEmployee} className="mb-6 flex flex-col sm:flex-row gap-2">
        <input
          className="border p-2 flex-1"
          placeholder="Employee Name"
          value={newEmployee.name}
          onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
        />
        <input
          className="border p-2 w-24"
          placeholder="Rate ($/hr)"
          type="number"
          min="0"
          value={newEmployee.rate}
          onChange={(e) => setNewEmployee({ ...newEmployee, rate: e.target.value })}
        />
        <button className="bg-green-600 text-white px-4 py-2 rounded" type="submit">Add</button>
      </form>

      {/* Default Local Tax Rate */}
      <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-2">
        <label className="text-sm font-medium">Default Local Tax Rate (%):</label>
        <input
          className="border p-2 w-20"
          type="number"
          min="0"
          max="10"
          value={defaultLocalTaxRate}
          onChange={e => setDefaultLocalTaxRate(Number(e.target.value))}
        />
        <span className="text-xs text-gray-500">(St. Louis/KC, override per entry)</span>
      </div>

      {/* Add Payroll Entry */}
      <form onSubmit={addPayroll} className="mb-6 flex flex-col sm:flex-row gap-2 flex-wrap items-end">
        <select
          className="border p-2 flex-1"
          value={newPayroll.employeeId}
          onChange={(e) => setNewPayroll({ ...newPayroll, employeeId: e.target.value })}
        >
          <option value="">Select Employee</option>
          {employees.map((emp) => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
        </select>
        <input
          className="border p-2 w-20"
          placeholder="Hours"
          type="number"
          min="0"
          value={newPayroll.hours}
          onChange={(e) => setNewPayroll({ ...newPayroll, hours: e.target.value })}
        />
        <div className="flex flex-col">
          <label className="text-xs text-gray-500">Period Start</label>
          <input
            className="border p-2 w-36"
            type="date"
            value={newPayroll.payPeriodStart}
            onChange={(e) => setNewPayroll({ ...newPayroll, payPeriodStart: e.target.value })}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500">Period End</label>
          <input
            className="border p-2 w-36"
            type="date"
            value={newPayroll.payPeriodEnd}
            onChange={(e) => setNewPayroll({ ...newPayroll, payPeriodEnd: e.target.value })}
          />
        </div>
        <input
          className="border p-2 w-24"
          placeholder="Local Tax % (opt)"
          type="number"
          min="0"
          max="10"
          value={newPayroll.localTax}
          onChange={(e) => setNewPayroll({ ...newPayroll, localTax: e.target.value })}
        />
        <button className="bg-blue-600 text-white px-4 py-2 rounded" type="submit">Log</button>
      </form>

      {/* Payroll Table */}
      <div className="overflow-x-auto">
        <table className="min-w-[700px] w-full border text-xs md:text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border">Employee</th>
              <th className="p-2 border">Period</th>
              <th className="p-2 border">Hours</th>
              <th className="p-2 border">Rate</th>
              <th className="p-2 border">Gross</th>
              <th className="p-2 border">Fed</th>
              <th className="p-2 border">State</th>
              <th className="p-2 border">SS</th>
              <th className="p-2 border">Med</th>
              <th className="p-2 border">Local</th>
              <th className="p-2 border">Total Deduct</th>
              <th className="p-2 border font-semibold">Net</th>
            </tr>
          </thead>
          <tbody>
            {payroll.map((entry) => {
              const emp = employees.find((e) => e.id === Number(entry.employeeId));
              const rate = emp ? emp.rate : 0;
              return (
                <tr key={entry.id}>
                  <td className="p-2 border">{getEmployeeName(entry.employeeId)}</td>
                  <td className="p-2 border">{entry.payPeriodStart} to {entry.payPeriodEnd}</td>
                  <td className="p-2 border">{entry.hours}</td>
                  <td className="p-2 border">${rate.toFixed(2)}</td>
                  <td className="p-2 border">${entry.gross?.toFixed(2)}</td>
                  <td className="p-2 border">${entry.federal?.toFixed(2)}</td>
                  <td className="p-2 border">${entry.state?.toFixed(2)}</td>
                  <td className="p-2 border">${entry.ficaSocial?.toFixed(2)}</td>
                  <td className="p-2 border">${entry.ficaMedicare?.toFixed(2)}</td>
                  <td className="p-2 border">${entry.local?.toFixed(2)}</td>
                  <td className="p-2 border">${entry.totalDeduction?.toFixed(2)}</td>
                  <td className="p-2 border font-semibold">${entry.net?.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Payroll;